/* The inventory register (ADR-0035). Four selector cards over one filterable table. */

.overview {
  grid-column: 1 / -1;
  min-width: 0;
}

/* **Fit, not four** (responsive pass, 2026-10-30). With a receipt open the drawer takes five of the
   twelve columns and the cards get what is left — about 570px — and four across was 130px each:
   "2,423.3 tCO₂e" ran out of its own card and the share beneath "1,686.5" and "736.8" was cut at
   the card's edge. A card is never narrower than 12rem and the strip wraps to as many rows as it
   needs: four across when there is room, two by two beside the drawer. */
.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
  gap: 14px;
}

/* A card is a **toggle**, so it looks pressable and says so to a screen reader through
   `aria-pressed`. The review's own rule: a non-interactive figure must not masquerade as a
   button, and the converse holds too — a control that acts must look like one.

   **Rank inside the card, 2026-10-25:** name and what it covers on one line each, then the figure
   as the largest thing in the block with its unit beside it rather than beneath, then the share of
   the whole as a quiet bar. Nothing is coloured by the figure: the bar is a mark in the scope's own
   hue (`--s1`, `--s2`), which are marks and never text. */
.card {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 12px 14px;
  text-align: left;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  cursor: pointer;
  font: inherit;
}

.card:hover {
  border-color: var(--accent-line);
  background: var(--panel-2);
}

.card:active {
  background: var(--panel-3);
}

.card:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 3px;
}

.cardOn {
  border-color: var(--accent);
  background: var(--panel);
}

.cardName {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

.cardSub {
  font-size: var(--t-min);
  color: var(--muted);
}

/* Figure and unit on one baseline. Not named `figure`: ui-check finds KPIs by that class. */
.cardRow {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 5px;
  margin-top: 8px;
  /* As tall as the figure's line even when the row holds a word and an icon (Scope 3), so the line
     beneath it starts at the same height in every card of the strip. */
  min-height: 31px;
  font-variant-numeric: lining-nums tabular-nums;
}

.cardValue {
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--ink);
}

.cardUnit {
  font-size: var(--t-min);
  color: var(--muted);
}

.cardAction {
  margin-top: auto;
  padding-top: 6px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--accent);
}

.grade {
  display: inline-flex;
  margin-top: 5px;
  padding: 2px 6px;
  border-radius: 5px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
  background: var(--panel-3);
}

.registerContext {
  margin: 0 0 12px;
  max-width: 75ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

.cardSkeleton {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-height: 134px;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.skeletonValue,
.skeletonNote {
  display: block;
  width: 65%;
  height: 26px;
  border-radius: 4px;
  background: var(--panel-3);
}

.skeletonNote {
  width: 42%;
  height: 12px;
}

.disclosure > summary {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  cursor: pointer;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--accent);
}

.disclosure > summary:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.disclosure > summary:active {
  color: var(--ink);
}

.disclosure > summary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 3px;
}

.disclosure[open] > summary {
  margin-bottom: 12px;
}

.disclosure[open] > summary svg {
  transform: rotate(90deg);
}

@media (max-width: 1100px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .cards {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cardShare {
  margin-left: auto;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

/* The share of the combined inventory, drawn. Decoration would be a bar that is the same in every
   card; this one is the scope's own proportion, so it distinguishes the cards from each other. */
.cardBar {
  display: block;
  height: 4px;
  margin-top: 8px;
  border-radius: 999px;
  background: var(--panel-3);
  overflow: hidden;
}

.cardFill {
  display: block;
  height: 100%;
  min-width: 0;
  border-radius: 999px;
}

.cardFill1 {
  background: var(--s1);
}

.cardFill2 {
  background: var(--s2);
}

.cardFill3 {
  background: var(--ink-2);
}

/* Deliberately not a number, and deliberately not a warning colour: an unassessed scope is a
   question nobody has answered, which is incompleteness disclosed rather than a fault somebody
   caused. The dashed circle is the state; the words say how much of the question is answered. */
.cardUnassessed {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: var(--t-head);
  font-weight: 600;
  color: var(--ink-2);
  font-variant-numeric: lining-nums tabular-nums;
}

/* **Wraps.** Seven controls in a row that cannot wrap is a row that overflows its panel at a
   tablet width and at 200% zoom — and the control pushed off the end is the one nobody can reach.
   The gate measures horizontal overflow at 400px; this is what keeps it true as filters are
   added. */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

/* What is in force, in words, with a way out of each one (EXP-21). Above the controls rather than
   inside them: a `<select>` showing its own value only answers somebody already looking at that
   dropdown, and the filter people forget is the one they set before they got here. */
/* What the year-on-year bridge narrowed the register to (a list of sites), above the tabs so it is
   read on every one of them. Quiet, like Coverage's: it explains a view, it does not warn. */
.siteBanner {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  padding: 6px 10px;
  margin-bottom: 9px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel-2);
  font-size: var(--t-dense);
  color: var(--ink-2);
}

.siteBanner > p {
  margin: 0;
  flex: 1 1 40ch;
  min-width: 0;
  overflow-wrap: anywhere;
}

.chips {
  /* A grid item of the shell's body now: the filters are in view on every tab, above the panel. */
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 9px;
}

.chipsLead {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  font: inherit;
  font-size: var(--t-min);
  color: var(--ink);
  background: var(--accent-w);
  border: 1px solid var(--accent-line);
  border-radius: 20px;
  cursor: pointer;
}

.chip:hover {
  border-color: var(--accent);
}

.chipLabel {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--accent);
}

.chipX {
  font-size: var(--t-dense);
  line-height: 1;
  color: var(--accent);
}

/* The glyph is decorative; the sentence is what a screen reader announces. A control whose whole
   meaning is a × is a control only a sighted mouse user can use. */
.chipHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* The export says what it will contain in its own label, so it goes in the panel's action slot
   beside the count rather than under the table. */
.export {
  padding: 5px 10px;
  font: inherit;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  cursor: pointer;
}

.export:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.export:active {
  background: var(--panel-3);
}

.export:disabled {
  cursor: wait;
  color: var(--disabled);
}

.export:focus-visible,
.link:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 3px;
}

.search {
  flex: 1 1 auto;
  min-width: 0;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--t-dense);
}

/* `thead th`, not `th` — EXP-39.
   Each row's first cell became a `<th scope="row">`, which is what associates the other five
   values with the activity they belong to for somebody listening rather than looking. Left as a
   bare `.table th` the header styling applied to it too, so the first column rendered small,
   uppercase and grey: a semantic fix that looked like a styling regression, which is the shape of
   change that gets reverted. */
.table thead th {
  text-align: left;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
  padding: 0 8px 6px 0;
  border-bottom: 1px solid var(--line);
}

.table td,
.table .rowHead {
  padding: 7px 8px 7px 0;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
  color: var(--ink);
}

/* And it is a cell, not a heading: `<th>` is bold and centred by default in every browser. */
.rowHead {
  font-weight: 400;
  text-align: left;
}

/* The stated render limit (EXP-39). Deliberately not `--warn`: a register showing its largest 500
   rows is working as designed and saying so, not a problem a reader has to resolve. */
.capped {
  margin: 10px 0 0;
  padding: 8px 10px;
  font-size: var(--t-min);
  color: var(--ink-2);
  background: var(--panel-2);
  border: 1px solid var(--line);
  border-radius: 6px;
}

/* The ways out of the opening cap (EXP-39).
 *
 * `display: block` with a top margin rather than inline in the sentence: a reader scanning for "can
 * I see the rest" should not have to find a link inside four lines of prose, and the two controls
 * sit together so the cheaper one is read first. 16px of gap because both are underlined text — two
 * adjacent underlines read as one control, and that is the whole of the separation. */
.cappedMore {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.right {
  text-align: right;
  padding-right: 0;
}

.sub {
  display: block;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.link:hover {
  text-decoration-thickness: 2px;
}

/* The explicit receipt control. 24px is the floor for any target a reader aims at (tokens.css
   `--target-min`), whatever sits beside it. */
.receiptButton {
  display: inline-flex;
  align-items: center;
  min-width: var(--target-min);
  min-height: var(--target-min);
  margin-left: 8px;
  padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
}

.receiptButton:hover {
  border-color: var(--accent);
}

.link:active {
  color: var(--ink);
}

/* Status carries a **word**, never only a colour — a release criterion rather than a preference,
   and the rule the source census already follows. */
.ok {
  color: var(--ink-2);
}

.broken {
  color: var(--crit);
}

/* Energy, beside the figures it comes from (F09). SECR reports energy as well as emissions and the
   number is not the sum of the kWh rows.

   **A caveat is small print, not body copy.** The sentences here are right and must stay - a
   partial figure that does not say so is the failure F09 was - but they were set at the same
   size, weight and measure as everything else, so six of them stacked read as a legal brief and
   the screen stopped looking like a product. KD, 2026-10-25: *"no page looks like saas, its all
   look like blog"*. The content did not change; its rank did.

   They are now a **keyed list** under the cards: a muted label on the left naming what the line is
   about, the sentence on the right at the annotation rank, a hairline between the lines. The figure
   keeps full ink and tabular numerals so columns of digits line up; the caveat drops to the muted
   ramp; and the measure is capped at 92ch so a wide viewport stops producing 200-character lines. */
.notes {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  border-top: 1px solid var(--line);
}

.noteRow {
  display: grid;
  grid-template-columns: 7.5rem minmax(0, 1fr);
  column-gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid var(--line);
}

.noteKey {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

.energy,
.basis {
  margin: 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
  font-variant-numeric: lining-nums tabular-nums;
}

.energy strong {
  color: var(--ink);
  font-variant-numeric: lining-nums tabular-nums;
}

/* What the figure leaves out, in the same sentence as the figure. **Muted rather than `--warn`:**
   an exclusion that is correctly disclosed is not a warning, and colouring every one amber made
   the screen look alarmed about its own honesty. `--warn` stays for things that genuinely need
   attention, which is what makes it mean anything when it does appear. Both clear WCAG AA. */
.energyOmitted {
  color: var(--muted);
}

/* **S3-04.** What a sector methodology would need, where somebody asking "why is there no
   number" is already looking. Styled as text rather than as a table of zeros on purpose: a
   grid with a dash in every cell reads as data that failed to load.

   **Rank, 2026-10-25.** A family was a grey box holding a paragraph and a list of paragraphs, which
   made a card inside a panel and four-line sentences at full ink. It is now a header line (the
   family, and what it needs) over rows that put the asset class on the left and its limitation on
   the right at the annotation rank. Every limitation and citation is still on screen. */
.packs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
}

.pack {
  padding-top: 10px;
  border-top: 1px solid var(--line);
}

.packName {
  margin: 0;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.packNeeds {
  margin: 2px 0 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

.packClasses {
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
}

.packClass {
  display: grid;
  grid-template-columns: minmax(11rem, 16rem) minmax(0, 1fr);
  column-gap: 14px;
  padding: 5px 0;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.packClassHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
}

.packClassName {
  color: var(--ink);
  font-weight: 600;
}

.packClassBody {
  min-width: 0;
  max-width: 92ch;
  color: var(--muted);
}

/* Required, optional and reported-separately are three different things to a filing, so they are
   three different chips rather than one grey "unsupported". */
.packTag {
  display: inline-block;
  padding: 0 6px;
  border-radius: 999px;
  font-size: var(--t-min);
  font-weight: 600;
  background: var(--panel-3);
  color: var(--ink-2);
}

.packTagRequired {
  background: var(--warn-w);
  color: var(--warn);
}

.packCite {
  display: block;
  margin-top: 1px;
  font-size: var(--t-min);
  color: var(--ink-2);
}

@media (max-width: 700px) {
  .packClass {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 1px;
  }

  .noteRow {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 1px;
  }
}

/* Two export controls side by side — a view and an editable file. They wrap on a narrow panel
   header rather than overflowing it, because a control pushed off the right edge is a control
   nobody knows is there. */
.exports {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

/* A filter whose own list could not be read. Inline in the control row, because that is where the
   missing dropdown was — a sentence at the top of the panel would not connect to the gap. */
.filterFailed {
  font-size: var(--t-min);
  color: var(--crit);
}

/* EXP-21's sixth dimension. **This was a sentence instead of a control until M-08 was answered on
   2026-10-12**, and the comment here said "the product simply does not hold the fact" — which has
   stopped being true, so both the name and the reason are corrected rather than left for the next
   reader to reason from. There is a control now; this note is what the control means. Muted and
   below the filter row, and still not a warning: nothing is wrong. */
.ownerNote {
  margin: 0 0 8px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
  max-width: 92ch;
}

/* The one row state worth a mark: a figure whose evidence chain breaks. A "Complete" row is the
   ordinary case and carries no icon, because a mark on every row distinguishes nothing. */
.brokenIcon {
  vertical-align: -2px;
  margin-right: 4px;
}

/* ------------------------------------------------------------------------------ tabs */

/* The tab panel is one grid item spanning the shell's body, so a tab's contents stack in it
   rather than being placed on the twelve columns one by one. */
.tabArea {
  grid-column: 1 / -1;
  min-width: 0;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
}

/* A panel is a column flex box with `min-height: 0`; in a stack with no fixed height it must keep
   its content height, or a long panel would be squeezed against its neighbours. */
.stack > * {
  flex: none;
}

/* ------------------------------------------------------------------------ the register */

.countLine {
  margin: 0 0 8px;
  font-size: var(--t-dense);
  color: var(--ink-2);
}

.countLine strong {
  color: var(--ink);
}

/* The table scrolls sideways inside its own box on a narrow screen, so the page never does. */
.tableWrap {
  max-width: 100%;
  overflow-x: auto;
}

/* **The column menu** is a disclosure that opens a small list under its summary. Above the narrow
   breakpoint the list floats; below it, it is drawn in the panel's own flow, because the panel is
   `overflow: hidden` and a floating list would be clipped by it. */
.colMenu {
  position: relative;
}

.colMenu > summary {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--panel);
  font-size: var(--t-dense);
  color: var(--ink-2);
  cursor: pointer;
  list-style: none;
}

.colMenu > summary::-webkit-details-marker {
  display: none;
}

.colMenu > summary:hover {
  border-color: var(--accent-line);
  background: var(--panel-2);
}

.colMenu > summary:active {
  background: var(--panel-3);
}

.colMenu > summary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.colList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
  font-size: var(--t-dense);
}

@media (width > 900px) {
  .colList {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 5;
    min-width: 160px;
    box-shadow: var(--shadow-drawer);
  }
}

.colItem {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  color: var(--ink);
  cursor: pointer;
}

.fullRow {
  grid-column: 1 / -1;
  margin: 0;
}

/* The register's reading notes, as one closed disclosure under the controls. */
.reading {
  margin: 0 0 10px;
}

/* Filter controls keep a readable width and wrap; a lone select stretching to the panel width reads
   as the most important control in the row. */
.filters select {
  flex: 0 0 auto;
  width: 168px;
}

/* On a phone the four scope cards stack, so each is made shorter: the "Explore" hint stays in the
   button's text (and so in its accessible name) but is drawn off-screen, and the figure sits
   closer to its label. */
@media (max-width: 560px) {
  .card {
    position: relative;
  }

  .cardAction {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .cardRow {
    margin-top: 4px;
  }
}
